/**
 * Design tokens.
 *
 * ADDITIVE ONLY. Nothing here modifies an existing palette entry, the 8px spacing base,
 * typography, or a component override. `theme.palette.*` is read in 739 places and
 * `useTheme()` in 120 files; changing an existing value would repaint the app, and this
 * repo has no test suite to catch the fallout. Every token below is new surface that
 * components opt into.
 *
 * The values are not invented — each one is the number the codebase already repeats,
 * given a name so it stops being copied by hand.
 */

export interface AppTokens {
  /**
   * Where each brand colour is allowed to appear. Measured, not guessed — the ratios
   * are in docs/design-system.md.
   *
   * The short version: purple is the primary and works everywhere in both modes.
   * Orange is an accent and a FILL — on a light surface it never carries text, a
   * border or a focus ring, because white-on-orange and orange-on-white are both
   * 2.72:1 against a 4.5:1 floor. On an orange fill the label is navy (7.20:1).
   */
  brand: {
    /** Fill only on light surfaces. Label on top must be `onAccent`. */
    accent: string;
    /** Label colour for anything sitting on an `accent` fill. */
    onAccent: string;
  };
  /**
   * Focus indicator. WCAG 2.2 raises focus from a nicety to a requirement: 2.4.11
   * asks that focus not be obscured and 1.4.11 that the indicator reach 3:1 against
   * its surroundings. Never `outline: none` without a replacement.
   */
  /**
   * The colour a button wears when pressing it destroys something.
   *
   * `palette.error.main` is `#FA896B`, a salmon, and on the confirm button it read as a
   * second shade of the brand orange rather than a warning — measured 1.15:1 against
   * `primary.main` in dark mode, which is no separation at all. Destruction needs to look
   * different from "OK", so it gets its own value instead of bending the error scale,
   * which every alert and validation message in the app also reads.
   *
   * Per mode, because one red cannot do both jobs: dark enough for a white label on the
   * light popup, light enough to clear 3:1 against the dark one.
   *   light `#C62828` + white  -> 5.62:1 label, 5.62:1 against `#fff`
   *   dark  `#EF5350` + navy   -> 5.63:1 label, 4.10:1 against `#202c33`
   */
  destructive: {
    light: { fill: string; onFill: string };
    dark: { fill: string; onFill: string };
  };
  focus: {
    /** Ring thickness in px. 2 is the minimum that survives a low-DPI screen. */
    width: number;
    /** Gap between the control and its ring, so the ring reads as separate. */
    offset: number;
  };
  /**
   * Two density scales. Comfortable for onboarding, settings and billing; compact for
   * tables, the inbox, kanban and superadmin, where people scan dozens of rows and air
   * costs them screenfuls. A screen picks one and does not mix them.
   */
  density: {
    comfortable: DensityScale;
    compact: DensityScale;
  };
  /** Corner radii as MUI spacing multiples, for `borderRadius` in `sx`. */
  radius: {
    sm: number;
    md: number;
    lg: number;
    pill: string;
  };
  /** Named stacking layers, replacing hand-written z-index numbers. */
  z: {
    dropdown: number;
    sticky: number;
    drawer: number;
    dialog: number;
    toast: number;
    /** The route-level blocking Spinner already sits here. */
    blockingSpinner: number;
  };
  /** Recurring layout measurements lifted from the components that repeat them. */
  layout: {
    /** `DialogContent` top padding that compensates for DialogTitle's own bottom padding. */
    dialogContentPaddingTop: number;
    /** Vertical padding of a table's loading/empty row. */
    tableStateRowPaddingY: number;
    /** Minimum height of a centred section-level loading block. */
    loadingBlockMinHeight: number;
    /** Width cap on empty-state description text. */
    emptyStateMaxWidth: number;
    /** Side of the circular icon badge in a card-style empty state. */
    emptyStateIconBadgeSize: number;
  };
}

/**
 * `md: 2` and `lg: 3` are the two values already passed as
 * `PaperProps={{ sx: { borderRadius: 2 | 3 } }}` in 33 dialog files.
 */
export interface DensityScale {
  /** Vertical padding inside a row or cell, as MUI spacing multiples. */
  rowPaddingY: number;
  /** Horizontal padding inside a row or cell. */
  rowPaddingX: number;
  /** Gap between stacked elements. */
  gap: number;
  /** Padding inside a card or panel. */
  sectionPadding: number;
  /** MUI `size` to pass to controls in this density. */
  controlSize: 'small' | 'medium';
}

export const tokens: AppTokens = {
  brand: {
    accent: '#FF7235',
    onAccent: '#040433',
  },
  destructive: {
    light: { fill: '#C62828', onFill: '#ffffff' },
    dark: { fill: '#EF5350', onFill: '#040433' },
  },
  focus: {
    width: 2,
    offset: 2,
  },
  density: {
    comfortable: {
      rowPaddingY: 2,
      rowPaddingX: 2,
      gap: 2,
      sectionPadding: 3,
      controlSize: 'medium',
    },
    compact: {
      rowPaddingY: 1,
      rowPaddingX: 1.5,
      gap: 1,
      sectionPadding: 2,
      controlSize: 'small',
    },
  },
  radius: {
    sm: 1,
    md: 2,
    lg: 3,
    pill: '999px',
  },
  z: {
    dropdown: 1100,
    sticky: 1200,
    drawer: 1300,
    dialog: 1400,
    toast: 1500,
    blockingSpinner: 9999,
  },
  layout: {
    dialogContentPaddingTop: 3,
    tableStateRowPaddingY: 4,
    loadingBlockMinHeight: 300,
    emptyStateMaxWidth: 400,
    emptyStateIconBadgeSize: 64,
  },
};

/**
 * The tokens carried by the active theme, falling back to the module value.
 *
 * `tokens` is merged into the theme in exactly one place, src/theme/Theme.tsx, and the
 * module augmentation below declares it required — which makes every consumer read
 * `theme.tokens.x` unguarded. Any theme built some other way therefore crashes them:
 * a nested ThemeProvider, a Storybook decorator, a test harness. The values are static,
 * so falling back to the module costs nothing and removes a whole class of crash.
 */
export function themeTokens(theme: { tokens?: AppTokens }): AppTokens {
  return theme.tokens ?? tokens;
}

declare module '@mui/material/styles' {
  interface Theme {
    tokens: AppTokens;
  }
  interface ThemeOptions {
    tokens?: AppTokens;
  }
}

export default tokens;
